import { AsyncPipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
  AccountInfo,
  AuthError,
  BrowserAuthError,
  BrowserAuthErrorMessage,
  BrowserUtils,
  EventError,
  EventType,
  InteractionStatus,
  Logger,
  LogLevel,
} from "@azure/msal-browser";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from "rxjs";
import {
  firstExistentValueFrom,
  isExistent,
  isNavigationStateChange,
} from "src/utils";
import { AlertsComponent } from "./core/alerts.component";
import { ApplicationInsightsService } from "./core/application-insights.service";
import { Permission } from "./core/auth";
import { passwordResetAuthority } from "./core/auth/auth.utils";
import { UserRegistrationService } from "./core/auth/user-registration.service";
import { UserService } from "./core/auth/user.service";
import { LoadingComponent } from "./core/elements/loading.component";
import { SitesService } from "./core/sites";
import { SpotTruckInspectionSettingsService } from "./partner/services/site-settings/spot-truck-inspection-settings.service";

@UntilDestroy()
@Component({
  selector: "yms-root",
  template: `
    @if (isNavigatingChanges | async) {
      <yms-loading></yms-loading>
    }
    @if (!isInIframe) {
      <router-outlet></router-outlet>
    }
    <yms-alerts></yms-alerts>
  `,
  styles: [
    `
      yms-loading {
        height: 100%;
        align-self: center;
      }
    `,
  ],
  imports: [LoadingComponent, RouterOutlet, AlertsComponent, AsyncPipe],
})
export class RootComponent implements OnInit {
  public constructor(
    private readonly applicationInsights: ApplicationInsightsService,
    private readonly msal: MsalService,
    private readonly msalBroadcast: MsalBroadcastService,
    private readonly router: Router,
    private readonly sites: SitesService,
    private readonly spotTruckInspectionSettings: SpotTruckInspectionSettingsService,
    private readonly user: UserService,
    private readonly userRegistration: UserRegistrationService,
  ) {}

  public readonly isInIframe = BrowserUtils.isInIframe();

  public readonly isNavigatingChanges = this.router.events.pipe(
    isNavigationStateChange(),
    // Mark as initially navigating in case we missed the "start" event.
    startWith(true),
    // Skip any duplicates in case we did see the "start" event.
    distinctUntilChanged(),
    // Get the start and then the stop. We don't want the whole page to show
    // loading on every navigation, only on the first page load.
    take(2),
    shareReplay(1),
  );

  public ngOnInit(): void {
    // Listen to account changes from other tabs.
    this.msal.instance.enableAccountStorageEvents();

    this.msalBroadcast.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        map(
          () =>
            this.msal.instance.getActiveAccount() ??
            this.msal.instance.getAllAccounts().at(0),
        ),
        // After login, this event will fire but there won't be any accounts
        // stored yet. Instead, we'll handle loading the user details in the
        // `msalSubject$` handler below.
        filter(isExistent),
        switchMap(async (account) => this.loadUserAccount(account)),
        untilDestroyed(this),
      )
      .subscribe();

    this.msalBroadcast.msalSubject$
      .pipe(untilDestroyed(this))
      .subscribe(async (event) => {
        switch (event.eventType) {
          case EventType.ACCOUNT_REMOVED: {
            if (this.msal.instance.getAllAccounts().length === 0) {
              // Logout of all tabs if any tab is logged out in.
              this.user.logout();
            }
            break;
          }
          case EventType.LOGIN_SUCCESS: {
            if (!event.payload.account) {
              throw new Error("Missing account info in login payload.");
            }
            this.loadUserAccount(event.payload.account).catch((error) => {
              this.applicationInsights.logException(error);
              // If something has failed during login, there's not much we can
              // do to recover so just ensure we're fully logged out and the
              // user can try again.
              this.user.logout();
            });

            await this.redirectToCompleteInspectionForm();

            break;
          }
        }

        // Handle password reset requests.
        // Error message: "The user has forgotten their password."
        if (isAuthErrorWithCode("AADB2C90118", event.error)) {
          this.msal.loginRedirect({
            authority: passwordResetAuthority,
            scopes: ["openid"],
          });
          return;
        }

        // Handle "cancel" click on sign up page.
        // Error message: "The user has cancelled entering self-asserted information."
        if (isAuthErrorWithCode("AADB2C90091", event.error)) {
          this.msal.loginRedirect();
          return;
        }

        // Handle case where user uses old login URL (e.g. from a bookmark) in
        // the middle of a valid login session for some reason.
        // Error message: "No cached authority found."
        // See also the `LoginComponent` for additional handling of stale login
        // page sessions.
        if (isBrowserAuthErrorWithCode("noCachedAuthorityError", event.error)) {
          this.msal.loginRedirect();
          return;
        }
      });

    this.applicationInsights.load();

    // Set a custom logger to track MSAL errors via App Insights
    this.msal.setLogger(
      new Logger({
        logLevel: LogLevel.Info,
        loggerCallback: (logLevel, message) => {
          switch (logLevel) {
            case LogLevel.Error:
              this.applicationInsights.logException(message);
              return;
            case LogLevel.Info:
              this.applicationInsights.logCustomEvent("MSAL Info", {
                message,
              });
              return;
            case LogLevel.Warning:
              this.applicationInsights.logCustomEvent("MSAL Warning", {
                message,
              });
              // eslint-disable-next-line no-console
              console.warn(message);
              return;
          }
        },
      }),
    );
  }

  private async redirectToCompleteInspectionForm(): Promise<void> {
    if (!this.user.permissions.has(Permission.CreateSpotTruckInspection)) {
      return;
    }

    const shouldCompleteAfterLogin = await firstExistentValueFrom(
      this.spotTruckInspectionSettings.shouldCompleteAfterLoginChanges,
    );

    if (shouldCompleteAfterLogin) {
      const inspectionFormLink = await firstValueFrom(
        this.sites.selectedChanges.pipe(
          map((site) => site.getRouteUrl("spot-truck-inspection/new")),
        ),
      );

      await this.router.navigateByUrl(inspectionFormLink);
    }
  }

  private async loadUserAccount(account: AccountInfo): Promise<void> {
    this.msal.instance.setActiveAccount(account);

    if (this.user.isValid) {
      return;
    }

    const userDetails = await firstValueFrom(
      this.userRegistration.userDetailsChanges,
    );

    this.user.load(account, userDetails);

    await this.user.loadRegistrationDetails(userDetails);

    await this.user.isLoaded;

    if (!this.user.isRegistered) {
      await this.router.navigateByUrl("/register");
      return;
    } else if (!this.user.isValid) {
      await this.router.navigateByUrl("/account-pending");
      return;
    }
  }
}

function isAuthErrorWithCode(
  code: string,
  value: EventError,
): value is AuthError {
  return (
    value instanceof AuthError &&
    (value.errorCode === code || value.errorMessage.indexOf(code) > -1)
  );
}

function isBrowserAuthErrorWithCode(
  code: keyof typeof BrowserAuthErrorMessage,
  value: EventError,
): value is BrowserAuthError {
  return (
    value instanceof BrowserAuthError &&
    value.errorCode === BrowserAuthErrorMessage[code].code
  );
}
